import React, { useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { useLocation, useNavigate } from 'react-router-dom';

import { InteractionStatus } from '@azure/msal-browser';
import ROUTES from '../../constants/routes';
import { Spin } from 'antd';

type LocationState = {
  from?: string;
};

// Helper function to check if the path is an internal path to prevent open redirect vulnerabilities
const isInternalPath = (path: string): boolean => {
  return (
    typeof path === 'string' &&
    path.startsWith('/') &&
    !path.startsWith('//') &&
    !path.includes(':')
  );
};

export const AuthorizationPage = () => {
  const [hasError, setHasError] = React.useState(false);
  const { instance, inProgress } = useMsal();
  const { state } = useLocation();
  const navigate = useNavigate();

  // This is safe from CSRF because it's appended to the state guid instead of replacing it
  // eslint-disable-next-line max-len
  // https://learn.microsoft.com/en-us/azure/active-directory/develop/msal-js-pass-custom-state-authentication-request
  const intendedRoute =
    (state as LocationState)?.from && isInternalPath((state as LocationState).from!)
      ? (state as LocationState).from!
      : ROUTES.HOME;

  // Handles the redirect logic
  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;

    instance
      .handleRedirectPromise()
      .then((result) => {
        // If the user has logged in,
        // then navigate to their intended route (or home page by default)
        if (result?.account) {
          const destination =
            result.state && isInternalPath(result.state) ? result.state : ROUTES.HOME;
          navigate(destination, { replace: true });

          // If the user has been successfully authenticated,
          // then navigate them to their intended route
        } else if (instance.getActiveAccount()) {
          navigate(intendedRoute, { replace: true });

          // If the user has not yet been authenticated,
          // then initiate the process
        } else {
          instance
            .loginRedirect({
              scopes: [`${instance.getConfiguration().auth.clientId}/.default`],
              state: intendedRoute,
            })
            .catch((err) => {
              console.error('Login redirect failed', err);
              setHasError(true);
            });
        }
      })
      .catch((err) => {
        console.error('Redirect handling failed', err);
        setHasError(true);
      });
  }, [inProgress, instance, navigate, intendedRoute]);

  if (hasError) {
    return <div>Something went wrong. Please reload and try again.</div>;
  }

  return <Spin />;
};
